import { SOURCES } from './sources';

export const MEAT_FISH = [
  {
    slug: "koed",
    shortTitle: "Kød og fjerkræ",
    title: "Hvor længe kan kød holde sig i fryseren?",
    metaTitle: "Hvor længe kan kød fryses? — Hakkekød, kylling og flæskesteg",
    metaDescription: "Oksekød holder ca. 10 mdr., kylling 8–10 mdr., magert svin 6 mdr. og hakkekød ca. 4 mdr. Få overblik over pakning, optøning og holdbarhed.",
    answer: "Oksekød og lammekød kan holde sig ca. 10 måneder, kylling 8–10 måneder, magert svinekød ca. 6 måneder og hakket kød ca. 4 måneder ved -18 °C.",
    steps: [
      "Pak altid fersk kød om fra supermarkedets flamingobakker til kraftige fryseposer.",
      "Tryk hakket kød helt fladt i posen før indfrysning; det fryser og tør op på den halve tid.",
      "Pres al luft grundigt ud af posen, eller vakuumpak kødet for at undgå frostskader.",
      "Tø altid kød op i en skål eller på en dyb tallerken på køleskabets nederste hylde ved højst 5 °C.",
      "Gennemsteg hakket kød og fjerkræ grundigt til en kernetemperatur på mindst 75 °C."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk, Arla og Fødevarestyrelsen",
    table: [
      { food: "Oksekød (hele stege, bøffer)", duration: "Cirka 10 måneder", pack: "Frysepose uden luft", thaw: "Køleskab (12–24 timer)", use: "Stegning, simreretter" },
      { food: "Lammekød (kølle, koteletter)", duration: "Cirka 10 måneder", pack: "Tætsluttende frysepose", thaw: "Køleskab", use: "Gennemstegning eller gryderetter" },
      { food: "Kylling (hel)", duration: "8–10 måneder", pack: "Dobbelt frysepose", thaw: "Køleskab i skål (24–36 timer)", use: "Gennemsteges til mindst 75 °C jf. Arla" },
      { food: "Magert svinekød (mørbrad, koteletter)", duration: "Cirka 6 måneder", pack: "Tæt frysepose", thaw: "Køleskab", use: "Stegning" },
      { food: "Fedt svinekød (flæskesteg, ribbensteg)", duration: "Cirka 3 måneder", pack: "Vakuum eller kraftig pose", thaw: "Køleskab", use: "Fedtet harsker hurtigere; steg med sprød svær" },
      { food: "Hakket kød (okse, svin, kalv)", duration: "Cirka 4 måneder", pack: "Trykt fladt i frysepose", thaw: "Køleskab på tallerken", use: "Skal altid gennemsteges" },
      { food: "Kødfars med væske og krydderier", duration: "Cirka 2 måneder", pack: "Frysepose", thaw: "Køleskab", use: "Formes til frikadeller og gennemsteges" },
      { food: "Røget kød", duration: "4–6 måneder", pack: "Tætsluttende pose", thaw: "Køleskab", use: "Koges eller steges" },
      { food: "Færdigretter med kød", duration: "1–3 måneder", pack: "Tætsluttende boks", thaw: "Køleskab eller gryde", use: "Varmes igennem til over 75 °C" }
    ],
    sections: [
      {
        h2: "Hvorfor harsker fedtet i kødet?",
        body: `Kød bliver ikke bakterielt fordærvet i en fryser, der holder uafbrudt -18 °C. Det er kødets fedtindhold, der afgør, hvor længe det kan ligge, før kvaliteten forringes.

Under frysning reagerer fedtstofferne langsomt med den ilt, der er til stede i emballagen. Denne iltningsproces (harskning) gør fedtet gulligt eller gråt og giver en ubehagelig, ram bismag. Mager oksefilet holder derfor i cirka 10 måneder ifølge Forbrugerrådet Tænk, mens fed flæskesteg bør bruges inden for 3 måneder.`
      },
      {
        h2: "Pakning: Ompak altid fersk kød fra supermarkedet",
        body: `Fersk kød fra supermarkedets køledisk sælges ofte i tynde plastbakker med en beskyttende gasatmosfære. Denne emballage er ikke fremstillet til frysning: Plasten bliver skør og revner let, og der er for meget fri luft omkring kødet.

Tag altid kødet ud af bakken og læg det i en kraftig frysepose godkendt til fødevarer. Pres al overskydende luft ud omkring kødet, før du lukker posen. Tænk anbefaler også at vakuumpakke kødet, hvis muligt, eller pakke i madpapir før fryseposen for at beskytte mod frostskader.`
      },
      {
        h2: "Tricket med fladtrykt hakkekød",
        body: `Hakket kød er en af de mest almindelige frostvarer i danske hjem, men ofte fryses det ned som en stor klump direkte i posen. En tyk klump tager lang tid at fryse ind i midten, og det tager over et døgn at tø den op i køleskabet.

Læg i stedet hakkekødet i en frysepose og tryk eller rul det ud, så det ligger i et fladt, tyndt lag. Pres luften ud og luk posen. Fladt kød fryser hurtigt igennem, danner mindre iskrystaller og tør op i køleskabet på kortere tid.`
      },
      {
        h2: "Sikker optøning og håndtering af kødsaft",
        body: `Tø altid kød op på en dyb tallerken eller i en skål på køleskabets nederste hylde ved højst 5 °C. Optøningsvæsken fra råt kød – særligt fjerkræ og svinekød – kan indeholde bakterier som campylobacter eller salmonella.

Fødevarestyrelsen råder til altid at holde råt kød og dets saft strengt adskilt fra spiseklare madvarer og vaske hænder, kniv og skærebræt omhyggeligt efter kontakt med råt kød.`
      }
    ],
    faq: [
      { q: "Kan man fryse kød i supermarkedets originale bakke?", a: "Nej, kødet bør pakkes om i en frysepose. Originalemballagen er ikke egnet til frost og fremmer dannelse af rimfrost på overfladen." },
      { q: "Må man genfryse optøet kød?", a: "Ifølge Forbrugerrådet Tænk kan mad genfryses, så længe det sker inden for samme døgn, og maden har været opbevaret i køleskabet i mellemtiden." },
      { q: "Hvorfor bliver kød gråt i fryseren?", a: "Grå eller blege pletter skyldes frysetørring (frostskade), hvor overfladen er udtørret af kold luft. Det er ufarligt at spise, men kødet bliver tørt i de områder." },
      { q: "Hvor længe kan flæskesteg ligge i fryseren?", a: "Flæskesteg indeholder meget fedt og holder sig i cirka 3 måneder på frost ifølge Forbrugerrådet Tænk, før fedtet harsker." }
    ],
    internalLinks: [
      { text: "Læs den komplette holdbarhedstabel for mad", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Regler for sikker optøning af kød", href: "/optoening" },
      { text: "Må man genfryse optøet kød?", href: "/kan-man-genfryse" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Frysning af fisk og skaldyr", href: "/fisk-og-skaldyr" },
      { text: "Flæskesteg og svinekød i fryseren", href: "/flaeskesteg" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.foedevarestyrelsenOpbevaring,
      SOURCES.arlaKylling
    ]
  },
  {
    slug: "fisk-og-skaldyr",
    shortTitle: "Fisk og skaldyr",
    title: "Hvor længe kan fisk og skaldyr holde sig i fryseren?",
    metaTitle: "Kan man fryse fisk og skaldyr? Laks, rejer og røget laks",
    metaDescription: "Mager fisk holder 3–6 mdr., fed fisk som laks 2–3 mdr. og røget fisk ca. 3 uger. Få råd om indfrysning, optøning og glasering af fisk.",
    answer: "Mager fisk holder 3–6 måneder på frost, fed fisk som laks og sild holder 2–3 måneder, og røget fisk holder cirka 3 uger.",
    steps: [
      "Frys altid fisk så frisk som muligt; friskhed ved indfrysning afgør smagen ved optøning.",
      "Glasér hele fiskefileter i koldt vand for at danne en beskyttende is-hinde, der modvirker udtørring.",
      "Pak fisken tæt i fryseposer uden luft, eller brug vakuumpakker for at forhindre iltning af fiskeolierne.",
      "Tø altid fisk langsomt op i køleskabet ved højst 5 °C på en dyb tallerken.",
      "Tilbered fisken umiddelbart efter optøning, da fisk fordærver hurtigere end kød."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Mager fisk (torsk, kuller, rødspætte, sej)", duration: "3–6 måneder", pack: "Frysepose uden luft / glaseret", thaw: "Køleskab langsomt", use: "Kogning, stegning, fiskefrikadeller" },
      { food: "Fed fisk (laks, ørred, sild, makrel)", duration: "2–3 måneder", pack: "Vakuum eller tæt frysepose", thaw: "Køleskab", use: "Gennemstegning eller ovnbagning" },
      { food: "Fiskefars (rørt fiskefars)", duration: "1–2 måneder", pack: "Frysepose", thaw: "Køleskab", use: "Gennemsteges til fiskefrikadeller" },
      { food: "Røget fisk (røget laks, makrel, sild)", duration: "Cirka 3 uger", pack: "Tætsluttende pose", thaw: "Køleskab", use: "Mister fasthed og bliver blød; god i varme retter" },
      { food: "Gravad laks", duration: "Uegnet til længere frys", pack: "Højst få dage i køleskab", thaw: "Køleskab", use: "Meget kort holdbarhed; bør spises frisk" },
      { food: "Røget ål", duration: "Cirka 3 uger", pack: "Tæt lukket", thaw: "Køleskab", use: "Fedtet harsker hurtigt pga. røgning og højt fedtindhold" },
      { food: "Skaldyr (rejer, muslinger)", duration: "Afhænger af produktet; følg pakken", pack: "Tæt pose, kogelage hældt fra", thaw: "Køleskab på køkkenrulle", use: "I køleskab skal frisk skaldyr spises samme dag jf. Tænk" },
      { food: "Friske østers og muslinger (rå i skal)", duration: "Uegnet til frysning", pack: "Dør og åbner sig ikke", thaw: "Sundhedsfare ved rå brug", use: "Må kun fryses hvis de koges/dampes og pilles først" }
    ],
    sections: [
      {
        h2: "Hvorfor harsker fed fisk så hurtigt?",
        body: `Fiskefedt består i høj grad af flerumættede omega-3-fedtsyrer. Disse fedtsyrer er kemisk set ustabile og reagerer let med ilt – selv ved frosttemperaturer på -18 °C.

Når fed fisk som laks, makrel eller sild ligger for længe i fryseren, oxiderer fedtstofferne. Det giver smag af tran, og fiskekødet bliver gulligt og tørt. Mager fisk som torsk og rødspætte indeholder under 1 % fedt og holder ifølge Forbrugerrådet Tænk i 3–6 måneder, mens fed fisk holder 2–3 måneder.`
      },
      {
        h2: "Metoden med vandglasering",
        body: `Et velafprøvet trick fra fiskehandlere er vandglasering. Læg uindpakkede fiskefileter på bagepapir i fryseren i 1–2 timer, indtil overfladen er frossen.

Dyp fileterne lynhurtigt i en skål med iskoldt vand, og læg dem straks tilbage i fryseren. Vandet fryser øjeblikkeligt til en tynd beskyttende ishinde over hele fiskens overflade. Læg derefter fileterne i en frysepose. Ishinden forhindrer kontakt mellem fiskekødet og luften i fryseren og modvirker frostskader.`
      },
      {
        h2: "Røget fisk og gravad laks",
        body: `Røget fisk har en forbavsende kort holdbarhed på frost: cirka 3 uger ifølge Forbrugerrådet Tænk. Røgningsprocessen og saltningen fremmer paradoksalt nok iltningen af fedtstofferne over tid på frost.

I køleskabet holder åbnet røget laks kun 2–3 dage og gravad laks 1–2 dage ifølge Tænk. Når frossen røget laks tør op, bliver den ofte blødere i konsistensen. Optøet røget laks fungerer derfor glimrende, hvis den skæres i strimler og vendes i en varm pastaret eller tærte.`
      },
      {
        h2: "Skaldyr og muslinger",
        body: `Ifølge Forbrugerrådet Tænk skal friske muslinger og rejer i køleskabet spises samme dag eller senest dagen efter køb. Friske, levende østers og muslinger må aldrig fryses rå i deres lukkede skaller.

Hvis du vil fryse muslinger ned, skal de dampes grundigt først, tages ud af skallen og fryses ned i en smule af den siede kogelage i en tætsluttende beholder.`
      }
    ],
    faq: [
      { q: "Hvor længe kan laks holde sig i fryseren?", a: "Fed fisk som laks holder sig i 2–3 måneder ved -18 °C ifølge Forbrugerrådet Tænk, før fiskeolierne harsker." },
      { q: "Hvorfor kan røget laks kun holde 3 uger på frost?", a: "Røgning og salt fremskynder iltningen af fedtet på frost ifølge Forbrugerrådet Tænk, og kødet mister sin faste struktur." },
      { q: "Hvor længe holder fersk fisk i køleskabet?", a: "Fersk fisk skal opbevares koldt ved maks 2 °C og spises samme dag. Ved almindelig køleskabstemperatur (5 °C) holder færdigpakket fisk kun 1 døgn efter åbning jf. Tænk." },
      { q: "Hvad er vandglasering af fisk?", a: "Fisken indfryses kort og dyppes i iskoldt vand for at danne en tynd beskyttende ishinde mod udtørring og iltning." }
    ],
    internalLinks: [
      { text: "Se den samlede holdbarhedstabel for fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Råd om optøning af fisk og kød", href: "/optoening" },
      { text: "Må man genfryse optøet mad?", href: "/kan-man-genfryse" },
      { text: "Frysning af kød og fjerkræ", href: "/koed" },
      { text: "Frysning af pålæg og pølser", href: "/paalaeg" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  },
  {
    slug: "paalaeg",
    shortTitle: "Pålæg og pølser",
    title: "Kan man fryse pålæg og pølser?",
    metaTitle: "Kan man fryse pålæg, leverpostej og pølser? — Fakta",
    metaDescription: "Pølser holder 2–3 mdr. på frost, leverpostej kan fryses, og spegepølse holder 14 dage på køl. Få overblik over holdbarhed og pakning af pålæg.",
    answer: "Ja, pølser holder 2–3 måneder på frost, og leverpostej kan udmærket fryses, mens skiveskåret pålæg mister saft ved optøning.",
    steps: [
      "Frys leverpostej i mindre portioner direkte i alubakken eller ompakket i fryseposer.",
      "Læg madpapir mellem skiver af skinke, rullepølse og spegepølse, så de ikke fryser sammen.",
      "Pølser (grillpølser, wienerpølser) fryses bedst fladt i poser og holder 2–3 måneder.",
      "Tø altid pålægget langsomt op i køleskabet ved højst 5 °C for at undgå bakterievækst.",
      "Brug optøet pålæg hurtigt efter optøning."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Pølser (grillpølser, frankfurtere)", duration: "2–3 måneder", pack: "Tætsluttende frysepose", thaw: "Køleskab eller direkte på pande", use: "Holder 2–3 måneder i fryseren ifølge Tænk" },
      { food: "Leverpostej (bagt eller rå)", duration: "Forlænger holdbarhed; brug løbende", pack: "Alubakke med låg / pose", thaw: "Køleskab (bag rå postej direkte fra frost)", use: "I køleskab holder åbnet postej 4–6 dage jf. Tænk" },
      { food: "Spegepølse (hel eller skiver)", duration: "Forlænger holdbarhed", pack: "Madpapir + frysepose", thaw: "Køleskab", use: "Holder cirka 14 dage i køleskab efter åbning jf. Tænk" },
      { food: "Kødpålæg (skinke, rullepølse)", duration: "Afhænger af produktet; følg pakken", pack: "Mellemlægspapir i pose", thaw: "Køleskab", use: "Holder 2–4 dage i køleskab efter åbning jf. Tænk" },
      { food: "Salater med mayonnaise (hønsesalat)", duration: "Uegnet til frysning", pack: "Mayonnaisen skiller", thaw: "Bliver vandet og klumpet", use: "Bør spises frisk fra køleskab" }
    ],
    sections: [
      {
        h2: "Leverpostej: Rå eller bagt?",
        body: `Leverpostej klarer frysning godt. Både færdigbagt leverpostej og rå leverpostejfars kan fryses ned, hvis du ikke når at spise den inden for holdbarhedsdatoen. I køleskabet holder åbnet leverpostej sig i 4–6 dage ifølge Forbrugerrådet Tænk.

Har du købt en bakke rå leverpostej, kan du fryse den direkte i alubakken. Den store fordel ved rå postej på frost er, at du kan sætte den direkte fra fryseren ind i en forvarmet ovn (læg blot 15–20 minutter til bagetiden). Så får du en friskbagt postej med fin smag.`
      },
      {
        h2: "Pølser og holdbarhed på frost",
        body: `Pølser kan holde sig i 2–3 måneder i fryseren ifølge Forbrugerrådet Tænks holdbarhedstabel.

Pølser indeholder ofte en del fedt. Over længere tid på frost kan fedtet langsomt begynde at ilte og harske, og pølseskindet kan blive mere sejt. Frys gerne pølserne fladt i en frysepose, så du kan tage det antal op, du skal bruge, uden at optø hele pakken.`
      },
      {
        h2: "Kødpålæg i køleskab og fryser",
        body: `Ifølge Forbrugerrådet Tænk holder almindeligt kødpålæg som skinke og rullepølse kun 2–4 dage i køleskabet, efter emballagen er brudt. Røget og tørret pålæg holder længere, og spegepølse holder cirka 14 dage i køleskabet indpakket i madpapir.

Kødpålæg kan fryses, hvis det er ved at nå datoen, men ved optøning vil skiverne ofte smide en del væske. Pak med mellemlægningspapir og dup skiverne med køkkenrulle før servering, eller brug resterne i en varm toast eller æggekage.`
      }
    ],
    faq: [
      { q: "Hvor længe kan pølser holde i fryseren?", a: "Pølser kan holde 2–3 måneder i fryseren ifølge Forbrugerrådet Tænk." },
      { q: "Hvor længe holder pålæg i køleskabet efter åbning?", a: "Skinke og rullepølse holder 2–4 dage, leverpostej 4–6 dage, og spegepølse ca. 14 dage i køleskabet ifølge Forbrugerrådet Tænk." },
      { q: "Kan man fryse rå leverpostej og bage den direkte?", a: "Ja, rå leverpostej i alubakke kan bages direkte fra frossen tilstand; forlæng blot bagetiden med 15–20 minutter." },
      { q: "Kan man fryse mayonnaise- og creme fraiche-salater?", a: "Nej, emulsionen i mayonnaise og surmælksprodukter skiller ved frost og bliver grynet og vandet." }
    ],
    internalLinks: [
      { text: "Læs om frysning af kød og fjerkræ", href: "/koed" },
      { text: "Holdbarhed i fryseren for alle fødevarer", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Sikker optøning i køleskabet", href: "/optoening" },
      { text: "Frysning af saucer og salater", href: "/saucer-og-salater" },
      { text: "Hvad der ikke tåler frost", href: "/hvad-kan-man-ikke-fryse" },
      { text: "Pølser i fryseren", href: "/poelser" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  },
  {
    slug: "flaeskesteg",
    shortTitle: "Flæskesteg og svinekød",
    title: "Kan man fryse flæskesteg?",
    metaTitle: "Kan man fryse flæskesteg? Holdbarhed i fryser og køleskab",
    metaDescription: "Ja. Fedt svinekød som flæskesteg holder ca. 3 mdr. i fryseren, magert svinekød ca. 6 mdr. og hakket kød ca. 4 mdr. Sådan pakker og optør du den.",
    datePublished: "2026-11-03",
    dateModified: "2026-11-03",
    answer: "Ja. Flæskesteg er fedt svinekød og kan holde sig cirka 3 måneder i fryseren. Magert svinekød holder cirka 6 måneder og hakket kød cirka 4 måneder.",
    steps: [
      "Pak flæskestegen om fra supermarkedets bakke til en frysepose lavet til mad, eller vakuumpak den.",
      "Pres så meget luft ud som muligt, før posen lukkes.",
      "Læg stegen i fryseren hurtigst muligt, og sørg for at fryseren holder mindst -18 °C.",
      "Tø stegen op i køleskabet i en høj skål eller et fad, så kødsaften ikke drypper på andre madvarer.",
      "Steg den optøede flæskesteg, og opbevar resterne i køleskabet i en lufttæt pose."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Rå flæskesteg (fedt svinekød)", duration: "Cirka 3 måneder", pack: "Vakuum eller frysepose uden luft", thaw: "Køleskab i høj skål", use: "Steges; resterne i køleskab i lufttæt pose" },
      { food: "Magert svinekød (fx koteletter)", duration: "Cirka 6 måneder", pack: "Tæt frysepose", thaw: "Køleskab", use: "Steges" },
      { food: "Hakket svinekød", duration: "Cirka 4 måneder", pack: "Mindre portioner i frysepose", thaw: "Køleskab på tallerken", use: "Steges igennem" },
      { food: "Fars med tilsætning", duration: "Cirka 2 måneder", pack: "Frysepose", thaw: "Køleskab", use: "Formes og steges" },
      { food: "Røget svinekød", duration: "4–6 måneder", pack: "Tætsluttende pose", thaw: "Køleskab", use: "Koges eller steges" },
      { food: "Stegt flæskesteg (rester)", duration: "Kan fryses; kvaliteten falder med tiden", pack: "Skiver i tæt frysepose", thaw: "Køleskab", use: "Varmes grundigt igennem" }
    ],
    sections: [
      {
        h2: "Hvor længe kan flæskesteg holde sig i fryseren?",
        body: `Cirka 3 måneder. Forbrugerrådet Tænk sætter fedt svinekød til cirka 3 måneder i fryseren og magert svinekød til cirka 6 måneder. Flæskesteg har et tykt fedtlag under sværen og hører derfor til den fede gruppe.

Hakket kød holder cirka 4 måneder, mens fars kun holder 2 måneder. Røget kød holder 4–6 måneder.

Tiderne er vejledende. Du bliver ikke syg af kød, der har ligget længere, men konsistens, duft og smag ændrer sig.`
      },
      {
        h2: "Hvor længe kan koteletter og hakket svinekød holde sig i fryseren?",
        body: `Koteletter og andet magert svinekød holder cirka 6 måneder. Hakket svinekød holder cirka 4 måneder.

Del hakket kød i mindre portioner, før du fryser det. Forbrugerrådet Tænk skriver, at selv en god fryser har svært ved at gennemfryse en stor klump kød ensartet, og at mindre portioner giver bedre kvalitet efter optøning.`
      },
      {
        h2: "Sådan pakker du flæskesteg til fryseren – også vakuumpakket",
        body: `Pak altid ferskvarer fra supermarkedet om i fryseposer lavet til mad, medmindre du har købt dem frosne. Ikke al emballage tåler frost.

Jo mindre luft der er om kødet, jo mindre iltning og jo færre frostskader. Poser er derfor bedre end bokse. Kan du vakuumpakke flæskestegen, er det ifølge Forbrugerrådet Tænk en stor fordel.

Skal stegen ligge længe, kan du pakke den i madpapir eller en papirpose og derefter i en frysepose. Det beskytter ekstra mod frostskader.

Frys stegen hurtigst muligt. Hurtig indfrysning giver mindre iskrystaller, og kødet mister mindre saft, når det tøs op.`
      },
      {
        h2: "Optøning af flæskesteg",
        body: `Tø flæskestegen op i køleskabet. Fødevarestyrelsen anbefaler køleskabet, fordi kulden hæmmer væksten af bakterier, kødet mister mindre væske, og kvaliteten bevares bedre.

Læg stegen i en plastikboks, en høj skål eller et fad, der er stort nok til at opsamle kødsaften. En stor steg tager lang tid om at tø op i køleskabet, så tag den op i god tid.

Brug ikke køkkenbordet. Overfladen bliver hurtigt varm, mens kernen stadig er frossen, og så kan der vokse bakterier frem.

Har du ikke tid, kan kødet tøs op i en tæt lukket pose i koldt vand eller i mikroovnen. Det egner sig mest til mindre stykker. Fødevarestyrelsen anbefaler da, at kødet varmebehandles grundigt eller spises hurtigst muligt efter optøning.`
      },
      {
        h2: "Hvor længe kan flæskesteg holde sig i køleskabet?",
        body: `Stegt flæskesteg kan holde sig cirka 5–6 dage i køleskabet, hvis den er varmet grundigt igennem og kølet hurtigt ned. Opbevar den i en pose eller anden lufttæt emballage.

Rå flæskesteg og koteletter opbevares i køleskabet i en pose eller lufttæt emballage. Se datomærkningen for, hvor længe det rå kød holder. Kød er mærket med „sidste anvendelsesdato“, og er datoen overskredet, skal det smides ud.

En rå steg, der er tøet op i køleskabet, bør steges snart. Hold køleskabet på 5 °C.`
      },
      {
        h2: "Kan man genfryse flæskesteg?",
        body: `Ja, hvis den er tøet op i køleskabet og fryses igen inden for samme døgn. Vær dog opmærksom på, at kvaliteten påvirkes.

Har stegen tøet op på køkkenbordet, skal den først steges eller varmes grundigt igennem og køles hurtigt ned, før den kan komme i fryseren igen. Står der på pakken, at varen ikke må genfryses, så følg det.`
      }
    ],
    faq: [
      { q: "Hvor længe kan flæskesteg holde sig i fryseren?", a: "Cirka 3 måneder. Forbrugerrådet Tænk giver fedt svinekød cirka 3 måneder og magert svinekød cirka 6 måneder." },
      { q: "Hvor lang tid tager det at tø en flæskesteg op?", a: "En hel steg tager lang tid i køleskabet, så tag den op i god tid. Tø den op i køleskabet i en høj skål, ikke på køkkenbordet." },
      { q: "Hvor længe kan stegt flæskesteg holde sig i køleskabet?", a: "Cirka 5–6 dage i en lufttæt pose, hvis den er varmet grundigt igennem og kølet hurtigt ned." },
      { q: "Er det en fordel at vakuumpakke flæskesteg?", a: "Ja. Forbrugerrådet Tænk kalder det en stor fordel at vakuumpakke kød inden nedfrysning, fordi mindre luft giver mindre iltning og færre frostskader." },
      { q: "Kan man genfryse flæskesteg?", a: "Ja, hvis den er tøet op i køleskabet og fryses igen inden for samme døgn. Kvaliteten påvirkes dog." }
    ],
    internalLinks: [
      { text: "Holdbarhed for alt kød og fjerkræ på frost", href: "/koed" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Den samlede tabel: hvor længe holder mad i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Sikker optøning i køleskabet", href: "/optoening" },
      { text: "Hvornår man må genfryse mad", href: "/kan-man-genfryse" },
      { text: "Pølser i fryseren", href: "/poelser" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.taenkNedfrysning,
      SOURCES.foedevarestyrelsenOptoening,
      SOURCES.taenkOptoening,
      SOURCES.foedevarestyrelsenOpbevaring
    ]
  },
  {
    slug: "poelser",
    shortTitle: "Pølser",
    title: "Hvor længe kan pølser holde sig i fryseren?",
    metaTitle: "Pølser i fryseren: holdbarhed, optøning og genfrysning",
    metaDescription: "Pølser holder 2–3 måneder på frost. Sådan pakker du pølser til fryseren, tør dem sikkert op og genfryser dem inden for samme døgn.",
    datePublished: "2026-11-03",
    dateModified: "2026-11-03",
    answer: "Pølser kan holde sig 2–3 måneder i fryseren.",
    steps: [
      "Pak pølserne om i en frysepose lavet til mad, medmindre de er købt frosne.",
      "Pres luften ud af posen, eller vakuumpak pølserne.",
      "Frys dem i små portioner, så du kun tager det op, du skal bruge.",
      "Tø pølserne op i køleskabet i en skål eller på en dyb tallerken.",
      "Genfrys kun pølser, der er tøet op i køleskabet, og kun inden for samme døgn."
    ],
    tableNote: "Kilde: Forbrugerrådet Tænk og Fødevarestyrelsen",
    table: [
      { food: "Pølser (fx grillpølser, wienerpølser)", duration: "2–3 måneder", pack: "Frysepose uden luft eller vakuum", thaw: "Køleskab i skål", use: "Steges eller varmes" },
      { food: "Røgede pølser", duration: "4–6 måneder (røget kød)", pack: "Tætsluttende pose", thaw: "Køleskab", use: "Varmes eller steges" },
      { food: "Pølser tøet op i køleskab", duration: "Kan genfryses inden for samme døgn", pack: "Frysepose", thaw: "Køleskab", use: "Kvaliteten påvirkes" }
    ],
    sections: [
      {
        h2: "Pølser i fryseren: holdbarhed",
        body: `Pølser holder 2–3 måneder på frost ifølge Forbrugerrådet Tænk. Røget kød holder 4–6 måneder.

Tiderne er vejledende. Pølser, der har ligget længere, gør dig ikke syg, men smag, duft og konsistens bliver ringere.

Fryseren skal holde mindst -18 °C, for at maden holder sig bedst.`
      },
      {
        h2: "Sådan pakker du pølser til fryseren",
        body: `Pak pølser fra køledisken om i fryseposer lavet til mad. Ikke al emballage tåler frost.

Pres så meget luft ud som muligt. Mindre luft giver mindre iltning og færre frostskader. Poser er derfor bedre end bokse, og vakuumpakning er en stor fordel.

Læg pølserne i ét lag i posen, eller frys dem i små portioner. Så fryser de hurtigere, og du kan tage det antal op, du skal bruge. Hurtig indfrysning giver mindre iskrystaller og saftigere pølser efter optøning.`
      },
      {
        h2: "Optøning af pølser",
        body: `Tø pølser op i køleskabet. Kulden hæmmer bakterievækst, og pølserne mister mindre væske. Læg dem i en skål eller på en dyb tallerken, så væsken ikke drypper på anden mad.

Lad dem ikke tø op på køkkenbordet. Overfladen bliver varm, før kernen er tøet, og så kan der vokse bakterier frem.

Skal det gå hurtigt, kan pølserne tøs op i en tæt lukket pose i koldt vand eller i mikroovnen. Fødevarestyrelsen anbefaler da, at de varmes grundigt igennem eller spises hurtigst muligt efter optøning. Lad ikke skålen med vand stå for længe.

Bruger du mikroovnen, så fjern al emballage først, og læg pølserne på en dyb tallerken. Dele af pølsen kan begynde at blive varme, mens andre stadig er frosne, så hold øje og vend dem undervejs. Mikroovnen egner sig bedst, når pølserne skal steges lige bagefter.`
      },
      {
        h2: "Kan man genfryse pølser?",
        body: `Ja, hvis pølserne er tøet op i køleskabet og fryses igen inden for samme døgn. Vær dog opmærksom på, at kvaliteten påvirkes.

Har pølserne tøet op på køkkenbordet, skal de først steges eller varmes grundigt igennem og køles hurtigt ned, før de kan fryses igen. Står der på pakken, at varen ikke må genfryses, så følg producentens råd.`
      },
      {
        h2: "Pølser i køleskabet",
        body: `Pølser er kød og mærket med „sidste anvendelsesdato“. Er datoen overskredet, skal de smides ud, også selvom de ser fine ud. Frys dem derfor i god tid før datoen.

Spegepølse holder cirka 14 dage i køleskabet efter åbning, pakket i madpapir. Stegte eller kogte pølser hører under tilberedt kød, der holder cirka 5–6 dage i køleskabet i lufttæt emballage, hvis det er varmet grundigt igennem og kølet hurtigt ned. Grillmad skal du ikke gemme.`
      }
    ],
    faq: [
      { q: "Hvor længe kan pølser holde sig i fryseren?", a: "2–3 måneder ifølge Forbrugerrådet Tænk. Røget kød holder 4–6 måneder." },
      { q: "Hvordan tør man pølser op?", a: "I køleskabet i en skål eller på en dyb tallerken. Skal det gå hurtigt, kan de tøs op i koldt vand i en tæt pose og derefter varmes grundigt igennem." },
      { q: "Kan man genfryse pølser?", a: "Ja, hvis de er tøet op i køleskabet og fryses igen inden for samme døgn. Kvaliteten påvirkes." },
      { q: "Kan man fryse pølser i den originale pakke?", a: "Pak dem helst om i en frysepose lavet til mad. Ikke al emballage fra køledisken tåler frost." }
    ],
    internalLinks: [
      { text: "Pålæg og leverpostej i fryseren", href: "/paalaeg" },
      { text: "Sådan fryser du mad rigtigt", href: "/saadan-fryser-du-mad" },
      { text: "Den samlede tabel: hvor længe holder mad i fryseren", href: "/hvor-laenge-holder-mad-i-fryseren" },
      { text: "Sikker optøning i køleskabet", href: "/optoening" },
      { text: "Hvornår man må genfryse mad", href: "/kan-man-genfryse" },
      { text: "Flæskesteg og svinekød i fryseren", href: "/flaeskesteg" }
    ],
    sources: [
      SOURCES.taenkHoldbarhed,
      SOURCES.taenkNedfrysning,
      SOURCES.foedevarestyrelsenOptoening,
      SOURCES.taenkOptoening
    ]
  }
];
